import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// Movimento bancário — GET /api/bank-movements, POST/PUT, categorize (§6, camelCase pinado).
export interface BankMovement {
  id: number;
  movementDate: string;
  valueDate: string | null;
  description: string;
  counterpartyName: string | null;
  amount: number;
  currencyCode: string;
  balance: number | null;
  accountLabel: string | null;
  categoryId: number | null;
  categoryCode: string | null;
  categoryName: string | null;
  isInternalTransfer: boolean;
  /** Marcado como "sem fatura": fica fora da reconciliação, por decisão do utilizador. */
  isExcludedFromReconciliation: boolean;
  isBankCharge: boolean;
  isCategorizedManually: boolean;
  /** Fornecedor reconhecido no descritivo; null = por associar. */
  supplierId: number | null;
  supplierName: string | null;
  /** Fornecedor proposto mas por confirmar (confiança média). Nunca vem com supplierId. */
  suggestedSupplierId: number | null;
  suggestedSupplierName: string | null;
  /** Confiança do reconhecimento, 0 a 1. */
  supplierMatchScore: number | null;
  /** "memory" | "ai" | "name" | "manual" — de onde veio. */
  supplierMatchSource: string | null;
  supplierMatchReason: string | null;
  /** Fornecedor lido no descritivo que ainda não está registado; null = nenhuma pista. */
  supplierNameHint: string | null;
  isSupplierMatchedManually: boolean;
  /** Cliente de uma entrada, associado à mão; null = por associar. */
  clientId: number | null;
  clientName: string | null;
  /** O cliente foi escolhido pelo utilizador; senão veio de uma entrada igual já associada. */
  isClientMatchedManually: boolean;
  created: string;
  lastModified: string | null;
}

// Categoria de movimento (reference table) — GET /api/bank-movements/categories.
export interface BankMovementCategory {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

// Erro por linha reportado pelo parser de CSV (não engolido).
export interface BankMovementRowError {
  line: number;
  message: string;
}

// Resultado do import de CSV — POST /import.
export interface ImportBankMovementsResult {
  importBatchId: string;
  imported: number;
  skippedDuplicates: number;
  categorized: number;
  internalTransfersFlagged: number;
  bankChargesFlagged: number;
  suppliersMatched: number;
  rowErrors: BankMovementRowError[];
  movements: BankMovement[];
}

// Corpo do POST/PUT manual (§6 — BankMovementPayload).
export interface BankMovementPayload {
  movementDate: string;
  valueDate: string | null;
  description: string;
  counterpartyName: string | null;
  amount: number;
  currencyCode: string;
  balance: number | null;
  accountLabel: string | null;
  categoryId: number | null;
}

// Filtros da lista — GET /api/bank-movements.
// A categoria é o único classificador: "Custos bancários" e "Transferências internas"
// são categorias como as outras, por isso filtram-se pelo dropdown de categoria (sem flags à parte).
export interface BankMovementFilters {
  categoryId?: number | null;
  from?: string | null;
  to?: string | null;
  accountLabel?: string | null;
  bankAccountId?: number | null;
  /** "inflow" só entradas, "outflow" só saídas. */
  direction?: "inflow" | "outflow" | null;
  /** Procura no descritivo, na contraparte e no nome da conta. Corre no servidor. */
  search?: string | null;
  pageNumber?: number | null;
  pageSize?: number | null;
}

/** Uma página de movimentos com os totais do conjunto filtrado inteiro (BankMovementListVm). */
export interface BankMovementList {
  items: BankMovement[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  totalInflow: number;
  totalOutflow: number;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export function getBankMovements(
  filters?: BankMovementFilters,
): Promise<BankMovementList> {
  const params = new URLSearchParams();
  if (filters?.categoryId != null) {
    params.set("categoryId", String(filters.categoryId));
  }
  if (filters?.from) params.set("from", filters.from);
  if (filters?.to) params.set("to", filters.to);
  if (filters?.accountLabel && filters.accountLabel.trim() !== "") {
    params.set("accountLabel", filters.accountLabel.trim());
  }
  if (filters?.bankAccountId != null) {
    params.set("bankAccountId", String(filters.bankAccountId));
  }
  if (filters?.direction) params.set("direction", filters.direction);
  if (filters?.search && filters.search.trim() !== "") {
    params.set("search", filters.search.trim());
  }
  if (filters?.pageNumber != null) {
    params.set("pageNumber", String(filters.pageNumber));
  }
  if (filters?.pageSize != null) {
    params.set("pageSize", String(filters.pageSize));
  }
  const query = params.toString();
  return request<BankMovementList>(
    `/api/bank-movements${query ? `?${query}` : ""}`,
  );
}

export function importBankMovements(
  file: File,
  accountLabel?: string | null,
): Promise<ImportBankMovementsResult> {
  const data = new FormData();
  data.append("file", file);
  if (accountLabel && accountLabel.trim() !== "") {
    data.append("accountLabel", accountLabel.trim());
  }
  // No Content-Type header — the browser sets the multipart boundary.
  return request<ImportBankMovementsResult>("/api/bank-movements/import", {
    method: "POST",
    body: data,
  });
}

export function createBankMovement(
  payload: BankMovementPayload,
): Promise<BankMovement> {
  return request<BankMovement>("/api/bank-movements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateBankMovement(
  id: number,
  payload: BankMovementPayload,
): Promise<BankMovement> {
  return request<BankMovement>(`/api/bank-movements/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Categoria escolhida à mão; ensina os movimentos iguais, e o resultado diz quantos seguiram. */
export function categorizeBankMovement(
  id: number,
  categoryId: number,
): Promise<BankMovementEntityResult> {
  return request<BankMovementEntityResult>(`/api/bank-movements/${id}/categorize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ categoryId }),
  });
}

export async function deleteBankMovement(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/bank-movements/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

export function recategorizeBankMovements(): Promise<{ updated: number }> {
  return request<{ updated: number }>("/api/bank-movements/recategorize", {
    method: "POST",
  });
}

export function getBankMovementCategories(): Promise<BankMovementCategory[]> {
  return request<BankMovementCategory[]>("/api/bank-movements/categories");
}

/** Resultado de correr o reconhecimento de fornecedores sobre os movimentos já importados. */
export interface RecognizeSuppliersResult {
  examined: number;
  matched: number;
  /** Movimentos cujo descritivo nomeia um fornecedor que ainda não está registado. */
  hinted: number;
}

/**
 * O que uma associação à mão deixou feito: o movimento como ficou e o que arrastou atrás. É o que
 * faz um clique valer por cinco linhas em vez de uma.
 */
export interface BankMovementEntityResult {
  movement: BankMovement;
  /** Outros movimentos que ficaram com a mesma entidade, por o descritivo dizer o mesmo. */
  alsoAssociated: number;
  /** Movimentos que ficaram com categoria, ou mudaram de categoria, por causa deste gesto. */
  categorized: number;
  /** Movimentos, este e os arrastados, que ficaram reconciliados por a fatura ser certa. */
  autoReconciled: number;
}

/** Uma passagem do que já se sabe por tudo o que estava pendente. */
export interface BankMovementEntityResolution {
  suppliersLinked: number;
  clientsLinked: number;
  categoriesApplied: number;
  autoReconciled: number;
}

/** Confirma a proposta, escolhe outro fornecedor, ou desassocia com supplierId null. */
export function matchBankMovementSupplier(
  id: number,
  supplierId: number | null,
): Promise<BankMovementEntityResult> {
  return request<BankMovementEntityResult>(`/api/bank-movements/${id}/supplier`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ supplierId }),
  });
}

/**
 * Aplica o que já se sabe (memória, fornecedores registados entretanto, clientes já associados) a
 * tudo o que está pendente. O ecrã pede-o ao abrir: POST /api/bank-movements/resolve-entities.
 */
export function resolveBankMovementEntities(): Promise<BankMovementEntityResolution> {
  return request<BankMovementEntityResolution>("/api/bank-movements/resolve-entities", {
    method: "POST",
  });
}

export function recognizeBankMovementSuppliers(): Promise<RecognizeSuppliersResult> {
  return request<RecognizeSuppliersResult>("/api/bank-movements/recognize-suppliers", {
    method: "POST",
  });
}

/** Marca (ou desmarca) o movimento como "sem fatura": fica fora da reconciliação. */
export function setBankMovementReconciliationExclusion(
  id: number,
  excluded: boolean,
): Promise<BankMovement> {
  return request<BankMovement>(`/api/bank-movements/${id}/reconciliation-exclusion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ excluded }),
  });
}

/** Associa o cliente a uma entrada, ou desassocia com clientId null. */
export function matchBankMovementClient(
  id: number,
  clientId: number | null,
): Promise<BankMovementEntityResult> {
  return request<BankMovementEntityResult>(`/api/bank-movements/${id}/client`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clientId }),
  });
}

export function createClientFromBankMovement(
  id: number,
  name: string,
): Promise<BankMovementEntityResult> {
  return request<BankMovementEntityResult>(`/api/bank-movements/${id}/client/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  }).then((result) => {
    invalidateLookup("clients");
    return result;
  });
}

/** Regista o fornecedor que o descritivo nomeia e associa-o ao movimento no mesmo gesto. */
export function createSupplierFromBankMovement(
  id: number,
  name: string,
): Promise<BankMovementEntityResult> {
  return request<BankMovementEntityResult>(`/api/bank-movements/${id}/supplier/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  }).then((result) => {
    invalidateLookup("suppliers");
    return result;
  });
}
